/*
 * <product-ribbon> — continuous horizontal marquee of product cards at a
 * constant drift speed. Pauses on hover / focus / drag, resumes when the
 * pointer leaves. No coupling to page scroll.
 *
 * Track content must be duplicated 2× by the section liquid so the seamless
 * wrap is invisible. Uses requestAnimationFrame + transform: translateX for
 * GPU-accelerated motion. Pauses when off-screen (IntersectionObserver),
 * respects prefers-reduced-motion.
 *
 * Wrapped in an IIFE + customElements.get guard because the section liquid
 * emits the <script> tag once per ribbon instance — five ribbons on one page
 * would otherwise trigger "Identifier 'ProductRibbon' has already been declared".
 */
(function () {
  if (customElements.get('product-ribbon')) return;

  class ProductRibbon extends HTMLElement {
  constructor() {
    super();
    this.track = null;
    this.direction = -1; // -1 = right→left, +1 = left→right
    this.baseSpeed = 40; // px/sec
    this.offset = 0;
    this.lastTime = 0;
    this.running = false;
    this.paused = false;
    this.rafId = null;
    this.halfWidth = 0;
    this.boundTick = (t) => this.tick(t);

    // Drag state — drag-to-pan still works for swiping cards into view, but
    // there is no momentum/throw boost. On release the ribbon resumes its
    // constant drift speed from wherever the user left it.
    this.isDragging = false;
    this.dragStartX = 0;
    this.dragStartOffset = 0;
    this.dragMoved = 0; // total |delta| over the drag, used to suppress clicks
  }

  connectedCallback() {
    this.track = this.querySelector('[data-product-ribbon-track]');
    if (!this.track) return;

    // Duplicate cards stay clickable (they drift into view as the loop runs)
    // but are aria-hidden, so keep their links and buttons out of the tab order.
    this.track
      .querySelectorAll('[data-ribbon-duplicate] a, [data-ribbon-duplicate] button, [data-ribbon-duplicate] input, [data-ribbon-duplicate] select, [data-ribbon-duplicate] textarea, [data-ribbon-duplicate] [tabindex]')
      .forEach((el) => el.setAttribute('tabindex', '-1'));

    this.direction = this.dataset.direction === 'right' ? 1 : -1;
    this.baseSpeed = parseFloat(this.dataset.baseSpeed) || 40;

    // Respect reduced motion — show the track but don't animate.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.classList.add('product-ribbon--static');
      return;
    }

    this.measure();
    if (this.direction === 1) this.offset = -this.halfWidth;

    this.bind();
    // Defer start until images may have laid out — start() also guarded by IO.
  }

  disconnectedCallback() {
    this.stop();
    this.unbind();
  }

  measure() {
    if (!this.track) return;
    this.halfWidth = this.track.scrollWidth / 2;
  }

  bind() {
    this.onResize = this.onResize.bind(this);
    this.onEnter = () => { this.paused = true; };
    this.onLeave = () => { if (!this.isDragging) this.paused = false; };
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onClickCapture = this.onClickCapture.bind(this);

    window.addEventListener('resize', this.onResize);

    this.addEventListener('mouseenter', this.onEnter);
    this.addEventListener('mouseleave', this.onLeave);
    this.addEventListener('focusin', this.onEnter);
    this.addEventListener('focusout', this.onLeave);

    // Drag scrolling — pointer events work for mouse + touch uniformly.
    this.addEventListener('pointerdown', this.onPointerDown);
    // Capture-phase click handler suppresses card navigation after a drag.
    this.addEventListener('click', this.onClickCapture, true);

    if ('IntersectionObserver' in window) {
      this.io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) this.start();
          else this.stop();
        });
      }, { rootMargin: '100px 0px' });
      this.io.observe(this);
    } else {
      this.start();
    }

    if ('ResizeObserver' in window) {
      this.ro = new ResizeObserver(() => {
        const prev = this.halfWidth;
        this.measure();
        if (prev > 0 && this.halfWidth > 0) {
          this.offset = (this.offset / prev) * this.halfWidth;
        }
      });
      this.ro.observe(this.track);
    }
  }

  unbind() {
    window.removeEventListener('resize', this.onResize);
    document.removeEventListener('pointermove', this.onPointerMove);
    document.removeEventListener('pointerup', this.onPointerUp);
    document.removeEventListener('pointercancel', this.onPointerUp);
    if (this.io) this.io.disconnect();
    if (this.ro) this.ro.disconnect();
  }

  onPointerDown(event) {
    // Mouse: left-button only. Touch/pen: always.
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    this.isDragging = true;
    this.paused = true;
    this.dragStartX = event.clientX;
    this.dragStartOffset = this.offset;
    this.dragMoved = 0;
    this.classList.add('product-ribbon--dragging');
    // Global listeners so the drag continues even if the pointer leaves the element.
    document.addEventListener('pointermove', this.onPointerMove);
    document.addEventListener('pointerup', this.onPointerUp);
    document.addEventListener('pointercancel', this.onPointerUp);
  }

  onPointerMove(event) {
    if (!this.isDragging || this.halfWidth <= 0) return;
    const dx = event.clientX - this.dragStartX;
    this.dragMoved = Math.abs(dx);

    this.offset = this.dragStartOffset + dx;
    // Wrap so we stay inside the canonical [-halfWidth, 0] window even mid-drag.
    while (this.offset > 0) this.offset -= this.halfWidth;
    while (this.offset <= -this.halfWidth) this.offset += this.halfWidth;
    this.track.style.transform = `translate3d(${this.offset}px, 0, 0)`;
  }

  onPointerUp() {
    if (!this.isDragging) return;
    this.isDragging = false;

    document.removeEventListener('pointermove', this.onPointerMove);
    document.removeEventListener('pointerup', this.onPointerUp);
    document.removeEventListener('pointercancel', this.onPointerUp);
    this.classList.remove('product-ribbon--dragging');

    // Resume constant drift only if the user isn't hovering / focused.
    if (!this.matches(':hover') && !this.matches(':focus-within')) {
      this.paused = false;
    }
  }

  // Suppress card-click navigation when the user drag-scrolled by a real distance.
  onClickCapture(event) {
    if (this.dragMoved > 6) {
      event.preventDefault();
      event.stopPropagation();
      this.dragMoved = 0;
    }
  }

  onResize() {
    const prev = this.halfWidth;
    this.measure();
    if (prev > 0 && this.halfWidth > 0) {
      this.offset = (this.offset / prev) * this.halfWidth;
    }
  }

  start() {
    if (this.running) return;
    this.running = true;
    this.lastTime = performance.now();
    this.rafId = requestAnimationFrame(this.boundTick);
  }

  stop() {
    this.running = false;
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  tick(now) {
    if (!this.running) return;
    const dt = Math.min(now - this.lastTime, 50); // clamp big gaps (background tab)
    this.lastTime = now;

    if (!this.paused && this.halfWidth > 0) {
      const pxPerMs = this.baseSpeed / 1000;
      this.offset += pxPerMs * dt * this.direction;

      if (this.direction === -1 && this.offset <= -this.halfWidth) {
        this.offset += this.halfWidth;
      } else if (this.direction === 1 && this.offset >= 0) {
        this.offset -= this.halfWidth;
      }

      this.track.style.transform = `translate3d(${this.offset}px, 0, 0)`;
    }

    this.rafId = requestAnimationFrame(this.boundTick);
  }
}

  customElements.define('product-ribbon', ProductRibbon);
})();
